import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTBytesToSDKBytes } from '@wix/sdk-runtime/transformations/bytes';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

const TRANSFORM_PATHS_0 = [{ path: 'profile.customFields.value.numValue' }];
const TRANSFORM_PATHS_1 = [{ path: 'profile.customFields.value.dateValue' }];
const TRANSFORM_PATHS_2 = [
  { path: 'identity.createdDate' },
  { path: 'identity.updatedDate' },
  { path: 'identity.identityProfile.customFields.value.dateValue' },
  { path: 'additionalData.*.dateValue' },
];
const TRANSFORM_PATHS_3 = [
  { path: 'identity.identityProfile.customFields.value.numValue' },
  { path: 'additionalData.*.numValue' },
];
const TRANSFORM_PATHS_4 = [
  { path: 'identity.createdDate' },
  { path: 'identity.updatedDate' },
  { path: 'identity.identityProfile.customFields.value.dateValue' },
];
const TRANSFORM_PATHS_5 = [
  { path: 'identity.identityProfile.customFields.value.numValue' },
];
const TRANSFORM_PATHS_6 = [{ path: 'body' }];

function resolveWixIamAuthenticationV1AuthenticationServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _: [
      {
        srcPath: '/_api/iam/authentication',
        destPath: '',
      },
    ],
    'users._base_domain_': [
      {
        srcPath: '/iam/wix/google',
        destPath: '/v1/sso/callback/root/0e6a50f5-b523-4e29-990d-f37fa2ffdd69',
      },
      {
        srcPath: '/authentication',
        destPath: '',
      },
      {
        srcPath: '/_api/iam/authentication/v1/sso/callback',
        destPath: '/v1/sso/callback',
      },
      {
        srcPath: '/_api/iam/authentication',
        destPath: '',
      },
      {
        srcPath:
          '/_api/iam/authentication/v1/sso/login-with-token-params-redirect/',
        destPath: '/v1/sso/login-with-token-params-redirect',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/iam/authentication',
        destPath: '',
      },
      {
        srcPath: '/iam/authentication',
        destPath: '',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/_api/iam/authentication',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/iam/authentication',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/iam/authentication',
        destPath: '',
      },
    ],
    'dev._base_domain_': [
      {
        srcPath: '/_api/iam/authentication',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/authentication',
        destPath: '',
      },
      {
        srcPath: '/_api/iam/authentication',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/iam/authentication',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/iam/authentication/v2/sign-on',
        destPath: '/v2/sign-on',
      },
      {
        srcPath: '/iam/authentication/v1/logout',
        destPath: '/v1/logout',
      },
      {
        srcPath: '/iam/authentication/v2/change-password',
        destPath: '/v2/change-password',
      },
      {
        srcPath: '/iam/authentication/v2/verify',
        destPath: '/v2/verify',
      },
      {
        srcPath: '/iam/authentication/v2/login',
        destPath: '/v2/login',
      },
      {
        srcPath: '/iam/authentication/v2/register',
        destPath: '/v2/register',
      },
      {
        srcPath: '/iam/authentication/v2',
        destPath: '/v2',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_identity_authentication';

/**
 * Registers a new member.
 *
 * Typically, after a successful registration, you generate and use member tokens for the
 * registered member so that subsequent API calls are called as part of a member session.
 * Use the `sessionToken` in the response to [get the site member's access and refresh tokens](https://dev.wix.com/docs/go-headless/coding/rest-api/visitors-and-members/handle-members-with-custom-login#get-the-site-members-access-and-refresh-tokens).
 *
 * If the email used to register the member already exists as a contact email, the registering
 * member need to verify the email address using a code that is sent to the address.
 */
export function registerV2(payload: object): RequestOptionsFactory<any> {
  function __registerV2({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: TRANSFORM_PATHS_0,
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: TRANSFORM_PATHS_1,
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.iam.authentication.v1.authentication',
      method: 'POST' as any,
      methodFqn: 'wix.iam.authentication.v1.AuthenticationService.RegisterV2',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamAuthenticationV1AuthenticationServiceUrl({
        protoPath: '/v2/register',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_2,
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: TRANSFORM_PATHS_3,
          },
        ]),
    };

    return metadata;
  }

  return __registerV2;
}

export function validateEmail(payload: object): RequestOptionsFactory<any> {
  function __validateEmail({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.authentication.v1.authentication',
      method: 'POST' as any,
      methodFqn:
        'wix.iam.authentication.v1.AuthenticationService.ValidateEmail',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamAuthenticationV1AuthenticationServiceUrl({
        protoPath: '/v2/validate-email',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __validateEmail;
}

/**
 * Logs in an existing member.
 *
 * Typically, after a successful login, you generate and use member tokens for the
 * logged-in member so that subsequent API calls are called as part of a member session.
 * Use the `sessionToken` in the response to [get the site member's access and refresh tokens](https://dev.wix.com/docs/go-headless/coding/rest-api/visitors-and-members/handle-members-with-custom-login#get-the-site-members-access-and-refresh-tokens).
 */
export function loginV2(payload: object): RequestOptionsFactory<any> {
  function __loginV2({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.authentication.v1.authentication',
      method: 'POST' as any,
      methodFqn: 'wix.iam.authentication.v1.AuthenticationService.LoginV2',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamAuthenticationV1AuthenticationServiceUrl({
        protoPath: '/v2/login',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_2,
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: TRANSFORM_PATHS_3,
          },
        ]),
    };

    return metadata;
  }

  return __loginV2;
}

/**
 * Changes the password of a logged-in member. The member must have a valid session to use this method.
 *
 * The member's session remains valid after password change.
 *
 * The new password must meet the site's configured password requirements for length
 * and complexity. These requirements are enforced automatically when processing the request.
 */
export function changePassword(payload: object): RequestOptionsFactory<any> {
  function __changePassword({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.authentication.v1.authentication',
      method: 'POST' as any,
      methodFqn:
        'wix.iam.authentication.v1.AuthenticationService.ChangePassword',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamAuthenticationV1AuthenticationServiceUrl({
        protoPath: '/v2/change-password',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __changePassword;
}

/**
 * Authenticates a member without credentials and returns a session token.
 *
 * This method provides authentication that automatically creates or updates
 * member accounts as needed during the authentication process.
 * Use this only for trusted integrations and scenarios where you already have verified user information.
 */
export function signOn(payload: object): RequestOptionsFactory<any> {
  function __signOn({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: TRANSFORM_PATHS_0,
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: TRANSFORM_PATHS_1,
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.iam.authentication.v1.authentication',
      method: 'POST' as any,
      methodFqn: 'wix.iam.authentication.v1.AuthenticationService.SignOn',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamAuthenticationV1AuthenticationServiceUrl({
        protoPath: '/v2/sign-on',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: TRANSFORM_PATHS_4,
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: TRANSFORM_PATHS_5,
          },
        ]),
    };

    return metadata;
  }

  return __signOn;
}

/**
 * Terminates the member's session and clears authentication cookies.
 *
 * Returns the following:
 * - `body`: HTML content for the logout response
 * - `statusCode`: HTTP status code (200 for success, 302 for redirect)
 * - `headers`: Array of key-value pairs including:
 * - `Set-Cookie` headers to remove all session cookies
 * - `Location` header (when `postLogoutRedirectUri` is provided, with 302 status)
 */
export function logout(payload: object): RequestOptionsFactory<any> {
  function __logout({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.authentication.v1.authentication',
      method: 'GET' as any,
      methodFqn: 'wix.iam.authentication.v1.AuthenticationService.Logout',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamAuthenticationV1AuthenticationServiceUrl({
        protoPath: '/v1/logout',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTBytesToSDKBytes,
            paths: TRANSFORM_PATHS_6,
          },
        ]),
    };

    return metadata;
  }

  return __logout;
}
